/**
 * music.ts — THE BACKGROUND MUSIC ENGINE.
 *
 * The scores themselves live in `tracks.ts`, one exported object each; this file
 * is the small engine that plays any of them. That split is the point: adding a
 * fifth tune is writing music, not writing software.
 *
 * ─── WHY THIS IS SYNTHESISED AND NOT AN MP3 ──────────────────────────────────
 *
 * This app ships no audio assets at all; every sound in the game is built from
 * oscillators. A looping music bed is the one place where the lazy answer — ship
 * a file and set `loop = true` — is genuinely tempting, and it is still the wrong
 * one here:
 *
 *   - A decoded stereo buffer of even fifteen seconds is several megabytes of
 *     resident memory, held for the whole session, on the phones that were
 *     already overheating.
 *   - Looped files almost never loop cleanly. Encoders pad the start with silence
 *     (MP3 famously; AAC too), so `loop` produces a small gap or click every pass
 *     — the exact seam a background bed must not have.
 *   - A FILE CANNOT RESPOND. Everything below — the bed tightening through the
 *     last five seconds of betting, opening up while the ball runs, ducking under
 *     the spoken result — is impossible with a recording. This is the real reason
 *     the whole thing is synthesised.
 *
 * Scheduled synthesis has no seam at all, because there is no loop point: bar 4
 * simply flows into bar 1 the way it flows into bar 2. The cost is about three
 * oscillator nodes a second, which next to the chip layer's fifty animating
 * elements a second is nothing.
 *
 * ─── HOW A BAR GETS PLAYED ───────────────────────────────────────────────────
 *
 * Four voices, stacked so each owns its own register and nothing fights:
 *
 *   BASS      one note a bar, sine, the root
 *   PAD       two notes, soft and slow, deliberately OVERLAPPING the barlines
 *   OSTINATO  the figure — a fixed rhythm and a fixed contour, indexing into
 *             whatever chord tones the current bar offers. That is what makes
 *             these ostinatos rather than chord loops: the shape stays put and
 *             the harmony moves underneath it.
 *   PULSE     the drive. Silent below half intensity; it is what turns a calm
 *             bed into an urgent one without changing a note.
 *
 * Plus two bells a pass, placed to decay across a barline so the bars knit.
 *
 * See `tracks.ts` for the scores and for the note on what actually makes a game
 * feel exciting — the short version is that it is contour, not tempo.
 */

import { audioCtx } from './sound';
import { TRACKS, DEFAULT_TRACK, trackById, passSeconds, type Track } from './tracks';

/* ─── engine ─────────────────────────────────────────────────────────────── */

/** Music sits UNDER everything. Chips, ball and speech all have to cut through. */
const MASTER = 0.55;
const G_BASS = 0.100, G_PAD = 0.032, G_OSTINATO = 0.075, G_BELL = 0.050, G_PULSE = 0.075;

const KEY = 'rlt_music';
let _track: Track = trackById(DEFAULT_TRACK);
let _on = true;
let _playing = false;
/* THE CURRENT RUN'S OUTPUT, and every oscillator belonging to it.
   Both are replaced wholesale on every start, and both are what stopMusic()
   takes away with it — see the long note there for why a shared gain node and an
   unregistered oscillator made two tracks play at once. */
let _gain: GainNode | null = null;
let _live: { o: OscillatorNode; end: number }[] = [];
let _t0 = 0;              // context time at which bar 0 of the current run begins
let _nextBar = 0;         // index of the next bar not yet scheduled
let _tick = 0;
let _intensity = 0.3;     // 0 = idle table, 1 = the ball is running
/* Context time until which the bed is DUCKED under the spoken call. While this
   is in the future, intensity changes update the voicing of newly scheduled
   bars but keep their hands off the gain -- see the note in setMusicIntensity. */
let _duckedUntil = 0;

const bar = () => (60 / _track.bpm) * 4;

/**
 * Read the saved preference. The stored value is a TRACK ID, or '0' for off, so
 * a player's choice of tune survives the session the same way their sound
 * setting does.
 */
export function initMusic() {
  try {
    const v = localStorage.getItem(KEY);
    if (v === '0') _on = false;
    else if (v) { _on = true; _track = trackById(v); }
  } catch { /* private mode */ }
  return _on;
}
export function isMusicOn() { return _on; }
export function currentTrack() { return _track; }
export function musicTracks() { return TRACKS; }

function persist() {
  try { localStorage.setItem(KEY, _on ? _track.id : '0'); } catch { /* private mode */ }
}

/**
 * ONE CONTROL FOR THE WHOLE THING. Tapping the Music tile walks Off → each track
 * in turn → Off, and the tile shows where you are by name. No extra screen, no
 * sub-menu, and a player who only ever wants silence gets there by tapping until
 * it says Off — which is the same gesture they would use for a plain toggle.
 */
export function cycleMusic() {
  const i = TRACKS.indexOf(_track);
  if (!_on) { _on = true; _track = TRACKS[0]; }
  else if (i >= TRACKS.length - 1) { _on = false; }
  else { _track = TRACKS[i + 1]; }
  persist();
  stopMusic();
  if (_on) startMusic();
  return { on: _on, track: _track };
}

/** Pick a track directly. */
export function setTrack(id: string) {
  _track = trackById(id);
  _on = true;
  persist();
  stopMusic();
  startMusic();
  return _track;
}

export function setMusicOn(on: boolean) {
  _on = on;
  persist();
  if (on) startMusic(); else stopMusic();
  return _on;
}

function gainNode(c: AudioContext): GainNode {
  if (!_gain) {
    _gain = c.createGain();
    _gain.gain.value = 0;
    _gain.connect(c.destination);
  }
  return _gain;
}

/** Register an oscillator against the current run so stopMusic() can kill it. */
function live(o: OscillatorNode, end: number) {
  _live.push({ o, end });
}

/* ─── intensity ──────────────────────────────────────────────────────────── */

/**
 * THE CONTOUR. This is the part that produces excitement rather than merely
 * accompanying it.
 *
 * Nothing about the music changes — same key, same tempo, same bars, so there is
 * never a seam or a switch to hear. What changes is how the bed is VOICED:
 *
 *   - the ostinato's filter opens, so it gets keener rather than just louder.
 *     High-frequency content is the strongest timbral cue for arousal there is,
 *     and it is the one a listener never consciously notices.
 *   - the drive voice fades in above half, adding a pulse that is felt more than
 *     heard, and disappears again before anyone can grow tired of it.
 *   - the whole bed lifts a little, but only a little. Loudness alone reads as
 *     someone turning a knob; brightness reads as the room getting busier.
 *
 * Values are ramped over 400 ms rather than set, so the tightening is something
 * a player feels happening to them rather than something they hear switch.
 */
export function setMusicIntensity(x: number) {
  _intensity = Math.max(0, Math.min(1, x));
  const c = audioCtx();
  if (!c || !_gain || !_playing) return;
  const now = c.currentTime;
  /* THE DUCK OUTRANKS THE CONTOUR. The result call and the phase change that
     accompanies it land in the same beat, so the intensity effect re-fires
     moments after duckMusic() -- and cancelScheduledValues below would erase
     the duck it had just scheduled, putting the bed back to full volume under
     the one piece of audio that carries information. While the duck is live,
     _intensity still updates (new bars take the right filter), but the gain
     schedule belongs to the duck. (2026-08-16 audit, C6.) */
  if (now < _duckedUntil) return;
  const target = MASTER * _track.trim * (0.78 + 0.22 * _intensity);
  _gain.gain.cancelScheduledValues(now);
  _gain.gain.setValueAtTime(Math.max(_gain.gain.value, 0.0001), now);
  _gain.gain.exponentialRampToValueAtTime(Math.max(target, 0.0001), now + 0.4);
}

/**
 * The round's shape, as one pure function, so the mapping lives in ONE place and
 * can be read at a glance:
 *
 *   window open, plenty of time   0.30   the table is just a room
 *   last five seconds              0.50 → 0.90   the count tightens it
 *   no more bets / ball running    1.00   wide open
 *   result on screen               0.45   release
 */
export function intensityFor(phase: string, secs: number): number {
  if (phase === 'arming' || phase === 'spinning') return 1;
  if (phase === 'result' || phase === 'reveal') return 0.45;
  if (phase === 'closing') return 0.95;
  if (secs >= 1 && secs <= 5) return 0.5 + (5 - secs) * 0.1;
  return 0.3;
}

/* ─── the voices ─────────────────────────────────────────────────────────── */

function voiceBass(c: AudioContext, out: GainNode, at: number, freq: number) {
  const B = bar();
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = 'sine';
  o.frequency.value = freq;
  // Slow in, slow out. A bass note that starts abruptly reads as a thud, and a
  // thud every few seconds is a pulse the player will start counting.
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(G_BASS, at + 0.18);
  g.gain.setValueAtTime(G_BASS, at + B * 0.55);
  g.gain.exponentialRampToValueAtTime(0.0001, at + B * 1.02);
  o.connect(g); g.connect(out);
  o.start(at); o.stop(at + B * 1.05); live(o, at + B * 1.05);
}

function voicePad(c: AudioContext, out: GainNode, at: number, freqs: [number, number]) {
  const B = bar();
  // One shared lowpass for both notes: a pad wants to be felt, not heard, and a
  // triangle above ~900 Hz is where it starts to be heard.
  const lp = c.createBiquadFilter();
  // 1200 rather than 900: at 900 the pad has no definition at all and reads as
  // hum. It still sits well under the ostinato.
  lp.type = 'lowpass'; lp.frequency.value = 1200; lp.Q.value = 0.6;
  const g = c.createGain();
  /* THE PAD HAS TO BREATHE, and the first version did not.
     It ran 1.25 bars with a 0.6 s attack, one per bar — so consecutive pads
     always overlapped by a quarter of a bar and there was never a moment with no
     pad sounding. Two detuned triangles through a dull filter, permanently on,
     in every track: a gapless low drone that never articulates.
     That is what the owner heard as "one continuous sound in the back end no
     matter which music I choose" — and he was right. It is also why the tracks
     were hard to tell apart: the least distinctive layer was the only one that
     never stopped, so it masked the ones that carried the actual identity.
     Now each bar's pad CLOSES before the next opens, bar 6% overlap for
     smoothness. The harmony still changes by fading rather than cutting; it just
     takes a breath in between, which is what makes each bar a chord rather than
     part of a wash. */
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(G_PAD, at + 0.32);
  g.gain.setValueAtTime(G_PAD, at + B * 0.62);
  g.gain.exponentialRampToValueAtTime(0.0001, at + B * 1.02);
  lp.connect(g); g.connect(out);

  for (const f of freqs) {
    const o = c.createOscillator();
    o.type = 'triangle';
    o.frequency.value = f;
    // A few cents apart so the two notes beat slowly against each other instead
    // of sitting dead still. Costs nothing, and it is the difference between a
    // pad and a test tone.
    o.detune.value = f === freqs[0] ? -5 : 5;
    o.connect(lp);
    o.start(at); o.stop(at + B * 1.06); live(o, at + B * 1.06);
  }
}

function voicePluck(c: AudioContext, out: GainNode, at: number, freq: number, vel: number) {
  const { osc, cut, open, decay } = _track.timbre;
  const o = c.createOscillator();
  const lp = c.createBiquadFilter();
  const g = c.createGain();
  o.type = osc;
  o.frequency.value = freq;
  lp.type = 'lowpass';
  // THE AROUSAL LEVER. Sampled at schedule time, so a note already in the air
  // keeps the colour it was born with and the change arrives note by note rather
  // than sweeping across everything at once — which is what makes it read as the
  // room warming up rather than as a filter being swept.
  lp.frequency.value = cut + open * _intensity;
  lp.Q.value = 0.7;
  const peak = G_OSTINATO * vel;
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, at + decay);
  o.connect(lp); lp.connect(g); g.connect(out);
  o.start(at); o.stop(at + decay + 0.05); live(o, at + decay + 0.05);
}

function voiceBell(c: AudioContext, out: GainNode, at: number, freq: number) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = 'sine';
  o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(G_BELL, at + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 2.2);
  o.connect(g); g.connect(out);
  o.start(at); o.stop(at + 2.3); live(o, at + 2.3);
}

/** The drive. Short, low, and only there when the round is tightening. */
function voicePulse(c: AudioContext, out: GainNode, at: number, freq: number, amount: number) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(freq * 1.5, at);
  // A quick downward sweep is what makes a sine read as a thump rather than a
  // beep — the same trick the game's own chip sounds use.
  o.frequency.exponentialRampToValueAtTime(freq * 0.7, at + 0.09);
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(G_PULSE * amount, at + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
  o.connect(g); g.connect(out);
  o.start(at); o.stop(at + 0.2); live(o, at + 0.2);
}

/** Lay one bar down on the audio clock. */
function scheduleBar(c: AudioContext, out: GainNode, index: number) {
  const t = _track;
  const B = bar();
  const b = t.chart[index % 4];
  const at = _t0 + index * B;
  const eighth = B / 8;

  voiceBass(c, out, at, b.bass);
  voicePad(c, out, at, b.pad);

  for (let i = 0; i < t.slots.length; i++) {
    voicePluck(c, out, at + t.slots[i] * eighth, b.tones[t.shape[i]], t.accent[i]);
  }
  for (const bl of t.bells) {
    if (bl.bar === index % 4) voiceBell(c, out, at + bl.slot * eighth, bl.freq);
  }
  // The drive fades in across the top half of the range and is simply absent
  // below it, so a calm table costs nothing at all to keep calm.
  if (t.pulse && _intensity > 0.5) {
    const amount = (_intensity - 0.5) * 2;
    const step = B / t.pulse.per;
    for (let i = 0; i < t.pulse.per; i++) {
      voicePulse(c, out, at + i * step, t.pulse.freq, amount * (i % 2 === 0 ? 1 : 0.6));
    }
  }
}

/**
 * A LOOKAHEAD SCHEDULER, not a timer per note.
 *
 * Every note above is placed on the AudioContext's own clock, which is sample
 * accurate and immune to main-thread jank — the wheel can stutter and the music
 * will not. All the JavaScript timer has to do is stay far enough ahead, so it
 * ticks twice a second and schedules anything starting within the next four
 * seconds. Two wake-ups a second, against the ~139 a second the chip emitter used
 * to create before this week's pass.
 *
 * The horizon is deliberately short as well as long enough: intensity is sampled
 * when a bar is SCHEDULED, so a four-second lookahead is also the longest the bed
 * can lag behind the round. That is under one bar — the tightening lands inside
 * the five-second count, which is the only place it has to.
 *
 * Bars are indexed from a fixed `_t0` rather than accumulated, so the loop cannot
 * drift no matter how late a tick arrives.
 */
function pump() {
  const c = audioCtx();
  if (!c || !_playing) return;
  const out = gainNode(c);
  const B = bar();
  const horizon = c.currentTime + 4;
  // If the tab was throttled and we have fallen behind, skip forward rather than
  // dumping a burst of overdue bars into the past.
  const earliest = Math.floor((c.currentTime - _t0) / B);
  if (_nextBar < earliest) _nextBar = earliest;
  while (_t0 + _nextBar * B < horizon) {
    scheduleBar(c, out, _nextBar);
    _nextBar++;
  }
  // Drop notes that have finished. The register only exists so a switch can cut
  // the future short, so anything already in the past is dead weight — and left
  // unpruned this would grow for the whole session.
  if (_live.length > 96) _live = _live.filter((n) => n.end > c.currentTime);
}

/** Begin, or resume after a stop. Safe to call repeatedly. */
export function startMusic() {
  if (!_on || _playing) return;
  const c = audioCtx();
  if (!c) return;
  const out = gainNode(c);
  _playing = true;
  _t0 = c.currentTime + 0.15;      // a beat of headroom so bar 0 is never late
  _nextBar = 0;
  // Fade in over a bar. Music that simply appears at full level is startling;
  // arriving under the room is how a real table sounds when you sit down.
  const target = MASTER * _track.trim * (0.78 + 0.22 * _intensity);
  out.gain.cancelScheduledValues(c.currentTime);
  out.gain.setValueAtTime(0.0001, c.currentTime);
  out.gain.exponentialRampToValueAtTime(target, c.currentTime + bar());
  pump();
  _tick = window.setInterval(pump, 500);
}

/**
 * STOP, PROPERLY — and the reason this is more than a fade.
 *
 * The first version of this did two things: cleared the scheduling timer and
 * ramped the gain down. Both were necessary and together they were nowhere near
 * enough, because of two facts that only bite when you SWITCH tracks rather than
 * simply stop:
 *
 *   1. The scheduler runs FOUR SECONDS AHEAD. At the moment of a switch the old
 *      track already has up to four seconds of notes committed to the audio
 *      clock, each with its own start time in the future. Clearing the timer
 *      stops new ones being written; it does nothing to the ones already there.
 *   2. Both runs shared ONE gain node. So when startMusic() ran a moment later
 *      and called cancelScheduledValues() on it — which it must, to fade the new
 *      track in — it also wiped out the fade-out that had just been scheduled on
 *      the old one. The old track's committed notes came back to FULL VOLUME and
 *      played on top of the new track, in a different key and a different tempo.
 *
 * So: each run now owns its gain node and a register of every oscillator it
 * created. Stopping ramps that run's own gain to nothing over 120 ms and calls
 * stop() on every oscillator in the register — including the ones scheduled for
 * the future, which the Web Audio spec simply never starts if their stop time is
 * at or before their start time. Then the node is disconnected and both are
 * replaced, so the next run cannot touch anything belonging to this one.
 *
 * 120 ms rather than an instant cut because a gain that goes to zero in one
 * sample is a click; 120 ms rather than the old 500 ms because when you are
 * comparing two tunes you want the first one GONE, not politely receding.
 */
export function stopMusic() {
  window.clearInterval(_tick);
  _tick = 0;
  _playing = false;
  /* The duck belongs to the run that scheduled it. Left standing, a track
     switch mid-announcement started the NEW run with the gate active but no
     return ramp scheduled on its fresh gain node. (Recheck P11.) */
  _duckedUntil = 0;

  const c = audioCtx();
  // Hand this run's chain to a local, and null the module's copies immediately:
  // from here on, anything that starts belongs to the NEXT run and must not be
  // able to reach these.
  const g = _gain, mine = _live;
  _gain = null; _live = [];
  if (!c || !g) return;

  const now = c.currentTime;
  const CUT = 0.12;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + CUT);
  for (const n of mine) {
    // Already-finished nodes throw nothing; future ones are cancelled outright.
    try { n.o.stop(now + CUT + 0.02); } catch { /* already stopped */ }
  }
  window.setTimeout(() => { try { g.disconnect(); } catch { /* gone */ } },
                    (CUT + 0.25) * 1000);
}

/**
 * DUCK, for the spoken result.
 *
 * The call — "number twenty-one, red" — is the one piece of audio in this game
 * that carries information, so everything else gets out of its way. This drops
 * the bed to a fifth over 180 ms and brings it back over three quarters of a
 * second, which is slow enough that the return is not itself an event.
 */
export function duckMusic(ms = 2200) {
  const c = audioCtx();
  if (!c || !_gain || !_playing) return;
  const now = c.currentTime;
  const base = MASTER * _track.trim * (0.78 + 0.22 * _intensity);
  _gain.gain.cancelScheduledValues(now);
  _gain.gain.setValueAtTime(Math.max(_gain.gain.value, 0.0001), now);
  _gain.gain.exponentialRampToValueAtTime(base * 0.2, now + 0.18);
  _gain.gain.setValueAtTime(base * 0.2, now + ms / 1000);
  _gain.gain.exponentialRampToValueAtTime(base, now + ms / 1000 + 0.75);
  _duckedUntil = now + ms / 1000 + 0.75;
}

/**
 * Backgrounding the app stops the music rather than playing it to an empty room.
 * The bed restarts from bar 0 on return, which for a loop with no beginning is
 * not something anyone can hear.
 */
export function bindMusicVisibility(): () => void {
  const onVis = () => {
    if (document.hidden) stopMusic();
    else if (_on) startMusic();
  };
  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}

/** Exposed for the gate test. */
export const _mix = { MASTER, G_BASS, G_PAD, G_OSTINATO, G_BELL, G_PULSE, passSeconds };
